import type {
  Booking,
  BookingCommercialInvoiceAnalysisSummary,
} from "../types/booking";
import { getPrimaryCommercialInvoicePath } from "../features/commercialInvoiceAnalysis/commercialInvoiceAnalysisService";
import { getAnalysisResultFromBookingEmbed } from "../features/commercialInvoiceAnalysis/commercialInvoiceValidity";

export const normalizeBookingCiAnalysisSummary = (
  value:
    | BookingCommercialInvoiceAnalysisSummary
    | BookingCommercialInvoiceAnalysisSummary[]
    | null
    | undefined
): BookingCommercialInvoiceAnalysisSummary | null => {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
};

/** Primary invoice file path with a valid cached analysis for that path. */
export const bookingHasCurrentCiAnalysis = (booking: Booking): boolean => {
  const primaryPath = getPrimaryCommercialInvoicePath(booking.commercial_invoice_file);
  if (!primaryPath) return false;

  const analysis = normalizeBookingCiAnalysisSummary(
    booking.commercial_invoice_analysis
  );
  return Boolean(analysis && analysis.object_path === primaryPath);
};

/** Has a stored invoice but no analysis (or analysis is stale after file change). */
export const bookingNeedsCiAnalysis = (booking: Booking): boolean => {
  const primaryPath = getPrimaryCommercialInvoicePath(booking.commercial_invoice_file);
  if (!primaryPath) return false;
  return !bookingHasCurrentCiAnalysis(booking);
};

/** Cached heuristic exists for current invoice but AI deep check was never saved. */
export const bookingHasDeepCheckCiAnalysis = (booking: Booking): boolean => {
  const primaryPath = getPrimaryCommercialInvoicePath(booking.commercial_invoice_file);
  if (!primaryPath) return false;

  const analysis = normalizeBookingCiAnalysisSummary(
    booking.commercial_invoice_analysis
  );
  if (!analysis || analysis.object_path !== primaryPath) return false;

  const result = getAnalysisResultFromBookingEmbed(booking);
  return Boolean(result?.deepCheck?.available);
};

export const bookingNeedsCiDeepCheck = (booking: Booking): boolean => {
  if (!bookingHasCurrentCiAnalysis(booking)) return false;
  return !bookingHasDeepCheckCiAnalysis(booking);
};

/** Either automatic or AI check still missing for the current invoice file. */
export const bookingNeedsAnyCiCheck = (booking: Booking): boolean =>
  bookingNeedsCiAnalysis(booking) || bookingNeedsCiDeepCheck(booking);
